import { useState, useEffect, useRef, useCallback } from 'react'
import { isIOS, isSpeechRecognitionSupported } from '../utils/videocall/deviceDetection'

/**
 * Custom hook for speech recognition
 *
 * @param {Function} onTranscriptFinal - Callback when final transcript is available
 * @param {Function} onTranscriptInterim - Callback for interim transcripts
 * @param {boolean} isEnabled - Whether recognition should be active
 * @param {boolean} isAudioEnabled - Whether microphone is enabled
 */
export function useSpeechRecognition({
  onTranscriptFinal,
  onTranscriptInterim,
  isEnabled = false,
  isAudioEnabled = true,
}) {
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [status, setStatus] = useState('Stopped')
  const recognitionRef = useRef(null)
  const shouldBeRunningRef = useRef(false)

  // Initialize recognition
  useEffect(() => {
    if (isIOS() || !isSpeechRecognitionSupported()) {
      setStatus(isIOS() ? 'Not supported on iOS' : 'Not supported')
      return
    }

    const SpeechRecognition =
      window.webkitSpeechRecognition || window.SpeechRecognition
    const recognition = new SpeechRecognition()

    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'
    recognition.maxAlternatives = 1

    recognition.onstart = () => {
      setIsTranscribing(true)
      setStatus('Listening')
    }

    recognition.onresult = (event) => {
      let finalTranscript = ''
      let interimTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript

        if (event.results[i].isFinal) {
          finalTranscript += transcript
        } else {
          interimTranscript += transcript
        }
      }

      if (finalTranscript && onTranscriptFinal) {
        onTranscriptFinal(finalTranscript)
      }

      if (interimTranscript && onTranscriptInterim) {
        onTranscriptInterim(interimTranscript)
      }
    }

    recognition.onerror = (event) => {
      console.error('[Speech Recognition] Error:', event.error)

      if (event.error === 'no-speech') {
        // Natural pause, will restart
        return
      } else if (event.error === 'not-allowed') {
        setStatus('Permission Denied')
      } else if (event.error === 'network') {
        setStatus('Network Error')
      } else if (event.error === 'aborted') {
        setStatus(isAudioEnabled ? 'Stopped' : 'Muted')
      } else {
        setStatus('Error')
      }
    }

    recognition.onend = () => {
      setIsTranscribing(false)

      // Restart if it should still be running
      if (shouldBeRunningRef.current && isAudioEnabled) {
        setStatus('Restarting...')
        setTimeout(() => {
          if (
            shouldBeRunningRef.current &&
            isAudioEnabled &&
            recognitionRef.current &&
            !isTranscribing
          ) {
            try {
              recognitionRef.current.start()
            } catch (e) {
              console.error('[Speech Recognition] Failed to restart:', e)
              // If restart fails, try to create a new recognition instance
              if (e.name === 'InvalidStateError') {
                // The useEffect will handle creating a new instance
                recognitionRef.current = null
              }
            }
          }
        }, 500) // Increased delay to prevent rapid restarts
      } else {
        setStatus(isAudioEnabled ? 'Stopped' : 'Muted')
      }
    }

    recognitionRef.current = recognition

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort()
        } catch (e) {
          // Ignore
        }
        recognitionRef.current = null
      }
    }
  }, [onTranscriptFinal, onTranscriptInterim, isAudioEnabled])

  // Start/stop recognition based on enabled state and audio state
  useEffect(() => {
    shouldBeRunningRef.current = isEnabled && isAudioEnabled

    if (!recognitionRef.current) {
      return
    }

    if (isEnabled && isAudioEnabled && !isTranscribing) {
      try {
        recognitionRef.current.start()
      } catch (e) {
        console.error('[Speech Recognition] Failed to start:', e)
        if (e.name === 'InvalidStateError') {
          // Recognition already started, ignoring error
        }
      }
    } else if ((!isEnabled || !isAudioEnabled) && isTranscribing) {
      try {
        recognitionRef.current.abort()
        setStatus(isAudioEnabled ? 'Stopped' : 'Muted')
      } catch (e) {
        console.error('[Speech Recognition] Failed to stop:', e)
      }
    }
  }, [isEnabled, isAudioEnabled, isTranscribing])

  const stop = useCallback(() => {
    shouldBeRunningRef.current = false
    if (recognitionRef.current && isTranscribing) {
      try {
        recognitionRef.current.abort()
      } catch (e) {
        console.error('[Speech Recognition] Failed to abort:', e)
      }
    }
  }, [isTranscribing])

  return {
    isTranscribing,
    status,
    stop,
    isSupported: isSpeechRecognitionSupported() && !isIOS(),
  }
}
